
const CONVERTIBLE_TYPES = new Set([
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/bmp',
  'image/tiff',
  'image/gif',
]);

const EXCLUDED_EXTENSIONS = new Set([
  'pdf', 'ai', 'psd', 'eps', 'svg', 'raw', 'cr2', 'nef', 'tif', 'webp',
]);

interface OptimizeOptions {
  quality?: number;
  maxDimension?: number;
}

export function isConvertibleImage(file: File): boolean {
  if (file.type === 'image/webp') return false;
  const ext = file.name.split('.').pop()?.toLowerCase() || '';
  if (EXCLUDED_EXTENSIONS.has(ext)) return false;
  return CONVERTIBLE_TYPES.has(file.type);
}

export async function convertToWebP(
  file: File,
  options: OptimizeOptions = {}
): Promise<File> {
  const { quality = 0.90, maxDimension = 2048 } = options;

  if (!isConvertibleImage(file)) return file;

  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);

      let { naturalWidth: w, naturalHeight: h } = img;
      if (w > maxDimension || h > maxDimension) {
        const ratio = Math.min(maxDimension / w, maxDimension / h);
        w = Math.round(w * ratio);
        h = Math.round(h * ratio);
      }

      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(file);
        return;
      }
      ctx.drawImage(img, 0, 0, w, h);

      canvas.toBlob(
        (blob) => {
          if (!blob || blob.type !== 'image/webp') {
            resolve(file);
            return;
          }
          const originalName = file.name.replace(/\.[^.]+$/, '');
          const webpFile = new File([blob], `${originalName}.webp`, {
            type: 'image/webp',
            lastModified: Date.now(),
          });
          resolve(webpFile);
        },
        'image/webp',
        quality
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(file);
    };

    img.src = url;
  });
}

export async function convertUrlToWebP(
  imageUrl: string,
  fileName: string,
  options: OptimizeOptions = {}
): Promise<File | null> {
  try {
    const response = await fetch(imageUrl);
    const blob = await response.blob();

    if (blob.type === 'image/webp') return null;

    const tempFile = new File([blob], fileName, { type: blob.type });
    if (!isConvertibleImage(tempFile)) return null;

    const converted = await convertToWebP(tempFile, options);
    return converted.type === 'image/webp' ? converted : null;
  } catch (error) {
    console.error('Error converting URL to WebP:', error);
    return null;
  }
}
